import { http } from '../http-common';
import type { AxiosRequestConfig } from 'axios';

export type ReminderStateType = 'PENDING' | 'PROCESSING' | 'ACCEPT' | 'REJECT' | 'COMPLETED' | 'CLOSED';
export type ActivityType =
  | 'CALL'
  | 'MEETING_OFFLINE'
  | 'MEETING_ONLINE'
  | 'EMAIL'
  | 'MESSAGE'
  | 'FOLLOW_UP'
  | 'TASK'
  | 'DOCUMENT'
  | 'REVIEW'
  | 'OTHER'
  | 'NOTE'
  | 'DISCOVERY'
  | 'PROPOSAL'
  | 'CONTRACT'
  | 'PLAN'
  | 'PROCUREMENT'
  | 'PAYMENT'
  | 'INVOICE'
  | 'LEGAL'
  | 'OFFICIAL'
  | 'INSPECTION'
  | 'REPORT'
  | 'FINANCE'
  | 'ACCOUNTING'
  | 'MAINTENANCE'
  | 'INFRASTRUCTURE'
  | 'SUPPORT'
  | 'MANAGEMENT_SYSTEM'
  | 'MARKETING'
  | 'BUSINESS_PARTNER'
  | 'SOLUTION_PARTNER'
  | 'HUMAN_RESOURCES'
  | 'COMPLAINT'
  | 'OBJECTION'
  | 'SATISFACTION'
  | 'CORRECTIVE_ACTION'
  | 'PREVENTIVE_ACTION';

export const ACTIVITY_TYPES: ActivityType[] = [
  'MANAGEMENT_SYSTEM',
  'MARKETING',
  'BUSINESS_PARTNER',
  'DISCOVERY',
  'PROPOSAL',
  'CONTRACT',
  'PLAN',
  'LEGAL',
  'INSPECTION',
  'REPORT',
  'ACCOUNTING',
  'FINANCE',
  'PROCUREMENT',
  'INFRASTRUCTURE',
  'SOLUTION_PARTNER',
  'HUMAN_RESOURCES',
  'COMPLAINT',
  'OBJECTION',
  // 'NOTE',
  'SATISFACTION',
  'PREVENTIVE_ACTION',
  'OTHER',
];

export const REMINDER_STATE_TYPES: ReminderStateType[] = ['PENDING', 'PROCESSING', 'ACCEPT', 'REJECT', 'COMPLETED'];
export const REMINDER_FILTER_STATE_TYPES: ReminderStateType[] = [...REMINDER_STATE_TYPES, 'CLOSED'];

export interface ReminderPayload {
  content?: string;
  entityName?: string;
  entityId?: string | number;
  reminderDate?: string;
  resolvedAt?: string;
  dueDate?: string;
  estimatedDurationMinutes?: number;
  properties?: Record<string, any> | string | null;
  activityType?: ActivityType;
  channel?: string;
  link?: string;
  result?: string;
  state?: ReminderStateType;
  statusId?: number | null;
  accountId?: number;
  tags?: string[];
}

export interface ReminderFilter {
  stateList?: ReminderStateType[];
  state?: ReminderStateType[];
  statusId?: number;
  search?: string;
  entityNames?: string | string[];
  entityId?: string | number;
  startDate?: string;
  endDate?: string;
  accountId?: string | number;
  page?: number;
  size?: number;
  sort?: string;
  // When true, merge the default activityType filter params into the request.
  useDefaultActivityFilter?: boolean;
  // Extra activity-related params (e.g. { 'activityType.in': 'STATUS_CHANGE' }).
  activityParams?: Record<string, any>;
  'tagId.in'?: number[];
  'estimatedDurationMinutes.specified'?: boolean;
}

export interface ReminderTagOption {
  id: number;
  name: string;
}

export interface ReminderDashboardSummary {
  prevCount: number;
  todayCount: number;
  nextCount: number;
}

type ReminderRequestOptions = Pick<AxiosRequestConfig, 'signal'>;

const serializeFilter = (params: ReminderFilter) => {
  const serialized: any = { ...params };
  if (Array.isArray(serialized.stateList)) {
    serialized.stateList = serialized.stateList.join(',');
  }
  if (Array.isArray(serialized.state)) {
    serialized.state = serialized.state.join(',');
  }
  delete serialized.useDefaultActivityFilter;
  delete serialized.activityParams;
  return serialized;
};

const DEFAULT_ACTIVITY_FILTER_PARAMS = {
  'activityType.in': 'STATUS_CHANGE,RESPONSIBLE_CHANGE,NOTE',
  'activityType.not': 'true',
};

const buildRequestParams = (params: ReminderFilter = {}) => {
  const serialized = serializeFilter(params);
  const result: Record<string, any> = { ...serialized };
  if (params.useDefaultActivityFilter) {
    Object.assign(result, DEFAULT_ACTIVITY_FILTER_PARAMS);
  }
  if (params.activityParams) {
    Object.assign(result, params.activityParams);
  }
  return result;
};

const buildQueryString = (params: Record<string, any>) => {
  const parts: string[] = [];
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
      });
      return;
    }
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  });
  return parts.join('&');
};

const appendQuery = (url: string, params: Record<string, any>) => {
  const query = buildQueryString(params);
  return query ? `${url}?${query}` : url;
};

const getMyReminders = (params: ReminderFilter, options: ReminderRequestOptions = {}) =>
  http.get(
    appendQuery('/reminders/proxy/me', buildRequestParams({ ...params, useDefaultActivityFilter: true })),
    options
  );

const getMyRemindersAll = (params: ReminderFilter) =>
  http.get(
    appendQuery('/accounts/me/reminders/all', buildRequestParams({ ...params, useDefaultActivityFilter: true }))
  );

const getMyRemindersAllRaw = (params: ReminderFilter) =>
  http.get(
    appendQuery('/accounts/me/reminders/all', buildRequestParams({ ...params, useDefaultActivityFilter: false }))
  );

const getAdminReminders = (params: ReminderFilter, options: ReminderRequestOptions = {}) =>
  http.get(
    appendQuery('/reminders/proxy', buildRequestParams({ ...params, useDefaultActivityFilter: true })),
    options
  );

const getAdminRemindersRaw = (params: ReminderFilter) =>
  http.get(
    appendQuery('/reminders/proxy', buildRequestParams({ ...params, useDefaultActivityFilter: false }))
  );

const getAdminRemindersProxyAll = (params: ReminderFilter) =>
  http.get(
    appendQuery('/reminders/proxy/all', buildRequestParams({ ...params, useDefaultActivityFilter: true }))
  );

const getAdminRemindersProxyAllRaw = (params: ReminderFilter) =>
  http.get(
    appendQuery('/reminders/proxy/all', buildRequestParams({ ...params, useDefaultActivityFilter: false }))
  );

const getAdminRemindersAll = (params: ReminderFilter) =>
  http.get(
    appendQuery('/reminders/all', buildRequestParams({ ...params, useDefaultActivityFilter: true }))
  );


const getMyStatusChangeReminders = (params: ReminderFilter) =>
  http.get(
    appendQuery(
      '/accounts/me/reminders/all',
      buildRequestParams({
        ...params,
        useDefaultActivityFilter: false,
        activityParams: { 'activityType.in': 'STATUS_CHANGE' },
      })
    )
  );

const getAdminStatusChangeReminders = (params: ReminderFilter) =>
  http.get(
    appendQuery(
      '/reminders/proxy',
      buildRequestParams({
        ...params,
        useDefaultActivityFilter: false,
        activityParams: { 'activityType.in': 'STATUS_CHANGE' },
      })
    )
  );

const getMyResponsibleChangeReminders = (params: ReminderFilter) =>
  http.get(
    appendQuery(
      '/reminders/proxy',
      buildRequestParams({
        ...params,
        useDefaultActivityFilter: false,
        activityParams: { 'activityType.in': 'RESPONSIBLE_CHANGE' },
      })
    )
  );

const getAdminResponsibleChangeReminders = (params: ReminderFilter) =>
  http.get(
    appendQuery(
      '/reminders/proxy',
      buildRequestParams({
        ...params,
        useDefaultActivityFilter: false,
        activityParams: { 'activityType.in': 'RESPONSIBLE_CHANGE' },
      })
    )
  );

const getMyNoteReminders = (params: ReminderFilter) =>
  http.get(
    appendQuery(
      '/accounts/me/reminders/all',
      buildRequestParams({
        ...params,
        useDefaultActivityFilter: false,
        activityParams: { 'activityType.in': 'NOTE' },
      })
    )
  );

const getAdminNoteReminders = (params: ReminderFilter) =>
  http.get(
    appendQuery(
      '/reminders/proxy',
      buildRequestParams({
        ...params,
        useDefaultActivityFilter: false,
        activityParams: { 'activityType.in': 'NOTE' },
      })
    )
  );

const getReminder = (id: number | string) => http.get(`/reminders/proxy/${id}`);

const createForMe = (data: ReminderPayload) => http.post('/accounts/me/reminders', data);

const createForAccount = (data: ReminderPayload) => http.post('/reminders', data);

const updateReminder = (id: number | string, data: ReminderPayload) => http.put(`/reminders/${id}`, data);

const remove = (id: number | string) => http.delete(`/reminders/${id}`);

const listReminderTags = (search?: string) =>
  http.get(
    appendQuery('/reminder-tags', {
      ...(search ? { search } : {}),
    })
  );

const getDashboardSummary = (mode: 'admin' | 'me' = 'me') =>
  http.get<ReminderDashboardSummary>(
    appendQuery('/reminders/proxy/dashboard-summary', { mode })
  );

const ReminderService = {
  getMyReminders,
  getMyRemindersAll,
  getMyRemindersAllRaw,
  getAdminReminders,
  getAdminRemindersRaw,
  getAdminRemindersProxyAll,
  getAdminRemindersProxyAllRaw,
  getAdminRemindersAll,
  getMyStatusChangeReminders,
  getAdminStatusChangeReminders,
  getMyResponsibleChangeReminders,
  getAdminResponsibleChangeReminders,
  getMyNoteReminders,
  getAdminNoteReminders,
  getReminder,
  listReminderTags,
  getDashboardSummary,
  createForMe,
  createForAccount,
  updateReminder,
  remove,
};

export default ReminderService;
